/* eslint-disable @typescript-eslint/no-unused-expressions */
/* eslint-disable class-methods-use-this */

import * as React from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import {
  Cell,
  CellTemplate,
  Uncertain,
  Compatible,
  getCellProperty,
  UncertainCompatible,
  keyCodes,
  getCharFromKeyCode,
  isAlphaNumericKey,
  isNavigationKey,
} from '@silevis/reactgrid';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuOptionGroup,
  Button,
  Text,
  IconButton,
} from '@chakra-ui/react';

import HorizontalMenu from 'components/Icons/horizontalMenu';
import DeleteTrashIcon from 'components/Icons/DeleteTrashIcon';
import { DriverPanelChevronIcon } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/icons/DriverPanelFigmaIcons';
import { usePlanRevampTable } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/PlanRevampTableContext';
import { PLAN_DRIVER_TABLE_PLACEHOLDER } from 'pages/PlanPageDetailsOPZ/PlanPageUIRevamp/planInputPanelTheme';
import { capitalizeFirstLetter } from 'utils/GlobalHelpers';

export type OptionType = {
  label: string;
  value: string;
};

export interface DropdownCell extends Cell {
  type: 'dropdown';
  selectedValue?: string;
  values: OptionType[];
  isDisabled?: boolean;
  isOpen?: boolean;
  inputValue?: string;
  buttonType?: number;
  planPermission?: boolean;
  selectOption?: boolean;
  placeholder?: string;
  validator?: (text: string) => boolean;
  isModernDropdown?: boolean;
}

// Scoped to the Dimensions "Items" table only (set via cell.isModernDropdown
// by NewTableHelpers.tsx / TableHelpers.tsx / TableHelpersopz.tsx) — this
// component is shared by 8 other tables, so the plain option-list styling
// stays untouched for them; only our table gets this modern look. Matches
// the same recipe as the Rename/Duplicate/Delete menu (DimensionItemTab.tsx)
// exactly — grey MenuList background + 1px gap between white items is what
// creates the divider lines — just without icons, since this is a plain
// option list.
const modernMenuOptionSx = {
  bg: 'white',
  borderRadius: '0 !important',
  padding: '9px 11px',
  fontFamily: 'Roboto Regular',
  fontSize: '13px',
  color: '#121826',
  _hover: { bg: '#F5F5F5' },
  _focus: { bg: 'white' },
  '&[aria-checked="true"]': { bg: 'white' },
  '.chakra-menu__icon-wrapper': { color: '#8B5CF6' },
};

const DropdownValueButton = ({ cell, className }: { cell: DropdownCell; className?: string }) => {
  const isRevampTable = usePlanRevampTable();
  const hasValue = Boolean(cell?.selectedValue);

  return (
    <MenuButton
      as={Button}
      className={`menu-cell ${className ?? ''}`}
      border='none'
      fontWeight={isRevampTable ? 700 : '500'}
      textDecoration='none'
      rightIcon={
        isRevampTable ? (
          <DriverPanelChevronIcon width={10} height={6} color='#8B5CF6' />
        ) : (
          <ChevronDownIcon fontSize='md' />
        )
      }
      h='100%'
      padding={isRevampTable ? 0 : '15px'}
      fontSize={isRevampTable ? '14px' : '15px'}
      fontFamily={isRevampTable ? 'bold' : undefined}
      color={isRevampTable ? (hasValue ? '#121826' : PLAN_DRIVER_TABLE_PLACEHOLDER) : 'white'}
      bg='transparent'
      textAlign='start'
      textTransform='none'
      boxShadow='none'
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'transparent' }}
    >
      {hasValue ? (
        <Text
          fontFamily={isRevampTable ? 'bold' : undefined}
          fontWeight={isRevampTable ? 700 : undefined}
        >
          {capitalizeFirstLetter(cell?.selectedValue ?? '')}
        </Text>
      ) : (
        <Text
          className={isRevampTable ? 'revamp-table-placeholder' : undefined}
          opacity={isRevampTable ? 1 : 0.2}
          fontFamily={isRevampTable ? 'bold' : undefined}
          fontWeight={isRevampTable ? 700 : undefined}
          color={isRevampTable ? PLAN_DRIVER_TABLE_PLACEHOLDER : undefined}
        >
          Select...
        </Text>
      )}
    </MenuButton>
  );
};

export class DropdownCellTemplate implements CellTemplate<DropdownCell> {
  onDeleteRow: (data: any) => void;

  planPermission?: boolean;

  constructor(onDeleteRow: (data: string) => void, planPermission: boolean) {
    this.onDeleteRow = onDeleteRow;
    this.planPermission = planPermission;
  }

  getCompatibleCell(uncertainCell: Uncertain<DropdownCell>): Compatible<DropdownCell> {
    let selectedValue: string | undefined;
    try {
      selectedValue = getCellProperty(uncertainCell, 'selectedValue', 'string');
    } catch {
      selectedValue = undefined;
    }
    const values = getCellProperty(uncertainCell, 'values', 'object');
    const value = selectedValue ? Number.parseFloat(selectedValue) : Number.NaN;
    let isDisabled = true;
    try {
      isDisabled = getCellProperty(uncertainCell, 'isDisabled', 'boolean');
    } catch {
      isDisabled = false;
    }
    let inputValue: string | undefined;
    try {
      inputValue = getCellProperty(uncertainCell, 'inputValue', 'string');
    } catch {
      inputValue = undefined;
    }
    let isOpen: boolean;
    try {
      isOpen = getCellProperty(uncertainCell, 'isOpen', 'boolean');
    } catch {
      isOpen = false;
    }
    const text = selectedValue || '';
    return { ...uncertainCell, selectedValue, text, value, values, isDisabled, isOpen, inputValue };
  }

  getClassName(cell: Compatible<DropdownCell>): string {
    const isValid = cell.validator ? cell.validator(cell.text) : true;
    const isOpen = cell.isOpen ? 'open' : 'closed';
    const className = cell.className || '';
    return `${className} ${isValid ? 'valid' : 'invalid'} ${
      cell.placeholder && cell.text === '' && 'placeholder'
    } ${isOpen}`;
  }

  update(
    cell: Compatible<DropdownCell>,
    cellToMerge: UncertainCompatible<DropdownCell>,
  ): Compatible<DropdownCell> {
    return this.getCompatibleCell({
      ...cell,
      selectedValue: cellToMerge.selectedValue,
      isOpen: cellToMerge.isOpen,
      inputValue: cellToMerge.inputValue,
    });
  }

  handleKeyDown(
    cell: Compatible<DropdownCell>,
    keyCode: number,
    ctrl: boolean,
    shift: boolean,
    alt: boolean,
  ): { cell: Compatible<DropdownCell>; enableEditMode: boolean } {
    if ((keyCode === keyCodes.SPACE || keyCode === keyCodes.ENTER) && !shift) {
      return {
        cell: this.getCompatibleCell({ ...cell, isOpen: !cell.isOpen }),
        enableEditMode: true,
      };
    }
    const char = getCharFromKeyCode(keyCode, shift);
    if (!ctrl && !alt && isAlphaNumericKey(keyCode))
      return {
        cell: this.getCompatibleCell({
          ...cell,
          inputValue: shift ? char : char.toLowerCase(),
          isOpen: !cell.isOpen,
        }),
        enableEditMode: false,
      };
    return { cell, enableEditMode: false };
  }

  render(
    cell: any,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<DropdownCell>, commit: boolean) => void,
  ): React.ReactNode {
    return (
      <Menu closeOnSelect={true} placement={cell?.buttonType && 'left'} strategy='absolute' isLazy>
        {({ isOpen }) => {
          return (
            <>
              {cell?.buttonType ? (
                <>
                  {String(cell?.className ?? '').includes('delete-column') ? (
                    <IconButton
                      aria-label='Delete row'
                      variant='ghost'
                      size='sm'
                      h='100%'
                      w='100%'
                      border='none'
                      onClick={
                        this.planPermission
                          ? undefined
                          : () => this.onDeleteRow({ id: cell?.id, itemName: cell?.itemName })
                      }
                      icon={<DeleteTrashIcon />}
                      isDisabled={Boolean(this.planPermission)}
                    />
                  ) : (
                    <MenuButton
                      border='none'
                      fontWeight={'500'}
                      textDecoration='none'
                      as={Button}
                      padding={'15px'}
                      h={'100%'}
                      w={'100%'}
                      textAlign='start'
                      textTransform='none'
                    >
                      <HorizontalMenu
                        width={'25px'}
                        height={'25px'}
                        styles={{ filter: 'invert(1)' }}
                      />
                    </MenuButton>
                  )}
                  {isOpen && !String(cell?.className ?? '').includes('delete-column') && (
                    <MenuList
                      width={'100%'}
                      padding={2}
                      zIndex={20}
                      ref={(input) => {
                        input && input.focus();
                      }}
                      onCopy={(event) => event.stopPropagation()}
                      onCut={(event) => event.stopPropagation()}
                      onPaste={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                      onKeyDown={(event) => {
                        if (isAlphaNumericKey(event.keyCode) || isNavigationKey(event.keyCode))
                          event.stopPropagation();
                      }}
                    >
                      {cell?.values?.map((option: any, index: number) => {
                        return (
                          <React.Fragment key={index}>
                            <MenuItem
                              height={'25px'}
                              cursor={this.planPermission ? 'not-allowed' : 'pointer'}
                              onClick={
                                this.planPermission
                                  ? undefined
                                  : () =>
                                      this.onDeleteRow({ id: cell?.id, itemName: cell?.itemName })
                              }
                            >
                              {option?.label}
                            </MenuItem>
                          </React.Fragment>
                        );
                      })}
                    </MenuList>
                  )}
                </>
              ) : (
                <>
                  <DropdownValueButton cell={cell} className={cell.className} />
                  {isOpen && (
                    <MenuList
                      p={'0'}
                      width={'100%'}
                      onPointerDown={(event) => {
                        event.stopPropagation();
                      }}
                      className={` scroll menu-cell ${cell.className}`}
                      maxH={'50vh'}
                      zIndex={20}
                      // minH={'fit-content'}
                      sx={{ overflow: 'auto' }}
                      bg={cell?.isModernDropdown ? '#E8E9EB' : undefined}
                      border={cell?.isModernDropdown ? 'none' : undefined}
                      borderRadius={cell?.isModernDropdown ? '14px' : undefined}
                      boxShadow={
                        cell?.isModernDropdown ? '0px 4px 24px rgba(0, 0, 0, 0.25)' : undefined
                      }
                    >
                      <MenuOptionGroup
                        as='ul'
                        display={cell?.isModernDropdown ? 'flex' : undefined}
                        flexDirection={cell?.isModernDropdown ? 'column' : undefined}
                        gap={cell?.isModernDropdown ? '1px' : undefined}
                        m={0}
                        value={cell?.selectedValue}
                        onChange={(event: any) =>
                          onCellChanged(
                            {
                              ...cell,
                              selectedValue: event,
                              text: event,
                            },
                            true,
                          )
                        }
                      >
                        {cell.selectOption && (
                          <MenuItemOption
                            value={''}
                            key={''}
                            sx={cell?.isModernDropdown ? modernMenuOptionSx : undefined}
                          >
                            Select...
                          </MenuItemOption>
                        )}
                        {cell?.values?.map((option: any) => {
                          return (
                            <MenuItemOption
                              as='li'
                              value={option?.value}
                              key={option.value}
                              cursor={this.planPermission ? 'not-allowed' : 'pointer'}
                              sx={cell?.isModernDropdown ? modernMenuOptionSx : undefined}
                            >
                              {option?.label}
                            </MenuItemOption>
                          );
                        })}
                        ;
                      </MenuOptionGroup>
                    </MenuList>
                  )}
                </>
              )}
            </>
          );
        }}
      </Menu>
    );
  }
}
